// Excerpt bodies are the small markdown subset `excerptMarkdown`
// (packages/corpus/src/pipeline/simplewiki/html-text.ts) writes: headings,
// paragraphs, `> ` quotes and `-`/`1.` lists indented two spaces per level,
// with plain text inside every block. Parsing it to data, rather than to
// HTML, means the reader renders it as React text nodes and nothing in an
// excerpt can ever reach the DOM as markup.

export interface ExcerptList {
  ordered: boolean;
  items: ExcerptListItem[];
}

export interface ExcerptListItem {
  text: string;
  sublist: ExcerptList | null;
}

export type ExcerptNode =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "quote"; text: string }
  | { kind: "list"; list: ExcerptList };

const HEADING = /^(#{1,6})\s+(.*)$/;
const LIST_ITEM = /^( *)(-|\d+\.)\s+(.*)$/;
const INDENT_PER_LEVEL = 2;

interface ListLine {
  depth: number;
  ordered: boolean;
  text: string;
}

function listLine(line: string): ListLine | null {
  const match = LIST_ITEM.exec(line);
  if (!match) return null;
  const [, indent = "", marker = "", text = ""] = match;
  return {
    depth: Math.floor(indent.length / INDENT_PER_LEVEL),
    ordered: marker !== "-",
    text: text.trim(),
  };
}

/** The list starting at `lines[from]`, at that line's depth, and the index after it. */
function buildList(
  lines: readonly ListLine[],
  from: number,
): { list: ExcerptList; next: number } {
  const depth = lines[from]?.depth ?? 0;
  const list: ExcerptList = {
    ordered: lines[from]?.ordered ?? false,
    items: [],
  };
  let i = from;
  while (i < lines.length) {
    const line = lines[i];
    if (!line || line.depth < depth) break;
    if (line.depth > depth) {
      const nested = buildList(lines, i);
      const parent = list.items.at(-1);
      if (parent) parent.sublist = nested.list;
      else list.items.push({ text: "", sublist: nested.list });
      i = nested.next;
      continue;
    }
    list.items.push({ text: line.text, sublist: null });
    i++;
  }
  return { list, next: i };
}

const joinLines = (lines: readonly string[]): string =>
  lines
    .map((l) => l.trim())
    .filter((l) => l !== "")
    .join(" ");

function parseChunk(chunk: string): ExcerptNode | null {
  const lines = chunk.split("\n");
  const first = lines[0]?.trim() ?? "";
  if (first === "") return null;

  const heading = HEADING.exec(first);
  if (heading && lines.length === 1) {
    return {
      kind: "heading",
      level: heading[1]?.length ?? 1,
      text: heading[2]?.trim() ?? "",
    };
  }
  if (lines.every((l) => l.trimStart().startsWith(">"))) {
    return {
      kind: "quote",
      text: joinLines(lines.map((l) => l.trimStart().replace(/^>\s?/, ""))),
    };
  }
  const items = lines.map(listLine);
  if (items.every((item): item is ListLine => item !== null)) {
    return { kind: "list", list: buildList(items, 0).list };
  }
  return { kind: "paragraph", text: joinLines(lines) };
}

export function parseExcerptMarkdown(body: string): ExcerptNode[] {
  return body
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n/)
    .map(parseChunk)
    .filter((node): node is ExcerptNode => node !== null);
}
